import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { WhitelabelConfiguration } from '../../models/whitelabel-config';
import { IOS_TRACKS, WhitelabelIosDeploymentTracks } from '../../models/whitelabel-ios-config';
import { ANDROID_TRACKS, WhitelabelAndroidDeploymentTracks } from '../../models/whitelabel-android-config';
import { androidTrackFormDetails, handleTrackControls, iosTracksFormDetails, tracksValidator } from '../../helpers/tracks.helper';

export interface BatchRunModalResponse {
  tracks: {
    ios: WhitelabelIosDeploymentTracks | null,
    android: WhitelabelAndroidDeploymentTracks | null
  },
  versionName: string | null;
  releaseNotes: string;
}

@Component({
  standalone: false,
  selector: 'app-whitelabel-batch-run',
  templateUrl: './whitelabel-batch-run.component.html',
  styleUrls: ['./whitelabel-batch-run.component.css']
})
export class WhitelabelBatchRunComponent {

  public androidTracks = ANDROID_TRACKS;

  public iosTracks = IOS_TRACKS;

  public readonly anyHasIos: boolean;

  public readonly anyHasAndroid: boolean;

  public batchRunForm: FormGroup;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { configurations: WhitelabelConfiguration[] },
    private dialogRef: MatDialogRef<WhitelabelBatchRunComponent>,
    private formBuilder: FormBuilder) {

    this.anyHasIos = data.configurations.some(c => c.hasIos());
    this.anyHasAndroid = data.configurations.some(c => c.hasAndroid());

    this.batchRunForm = this.formBuilder.group({
      executeIos: new FormControl(this.anyHasIos),
      executeAndroid: new FormControl(this.anyHasAndroid),
      iosTrack: new FormControl(iosTracksFormDetails.initialTrack),
      androidTrack: new FormControl(androidTrackFormDetails.initialTrack),
      versionName: new FormControl(null),
      releaseNotes: new FormControl(null, Validators.required)
    }, {
      validators: tracksValidator
    });

    handleTrackControls(this.batchRunForm);
  }

  public close() {
    this.dialogRef.close(null);
  }

  public onSubmit() {
    if (!this.batchRunForm.valid) {
      this.batchRunForm.markAllAsTouched();
      return;
    }

    const response: BatchRunModalResponse = {
      tracks: {
        ios: this.executeIos ? this.iosTrack : null,
        android: this.executeAndroid ? this.androidTrack : null
      },
      versionName: this.versionName?.trim() ? this.versionName.trim() : null,
      releaseNotes: this.releaseNotes
    };

    this.dialogRef.close(response);
  }

  public get executeIos() {
    return this.batchRunForm.controls['executeIos']?.value;
  }

  public get executeAndroid() {
    return this.batchRunForm.controls['executeAndroid']?.value;
  }

  private get iosTrack() {
    return this.batchRunForm.controls['iosTrack']?.value;
  }

  private get androidTrack() {
    return this.batchRunForm.controls['androidTrack']?.value;
  }

  private get versionName() {
    return this.batchRunForm.controls['versionName']?.value as string | null;
  }

  private get releaseNotes() {
    return this.batchRunForm.controls['releaseNotes']?.value;
  }
}
